import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import CartItemQuantity from 'components/Cart/CartItemQuantity';

import { getVariantPrice } from 'helpers/forms/menuItem';
import { ITEM_DESCRIPTION_AND_PRICES } from 'styles/constants/appearanceSectionNames';
import { StyledTypographyColorSectionNavLinkMainMedium } from 'components/StyledTypographyColorSectionNavLinkMainMedium';
import { StyledTypographyColorSectionNavLinkMain } from 'components/Cart/StyledTypographyColorSectionNavLinkMain';

const StyledCartModifier = styled(
  StyledTypographyColorSectionNavLinkMainMedium
)({
  lineHeight: 1.5
});

const StyledCartVariantName = styled(
  StyledTypographyColorSectionNavLinkMainMedium
)({
  display: 'block',
  lineHeight: 1.5
});

function RequiredFields({ requiredFields }) {
  const styles = { variant: ITEM_DESCRIPTION_AND_PRICES };
  return (
    <List disablePadding dense>
      {requiredFields.map(field => (
        <ListItem
          key={field.slug}
          disableGutters
          dense
          sx={{ display: 'block', padding: 0, lineHeight: 0 }}
        >
          <StyledCartModifier component="span" {...styles}>
            {field.name}:
          </StyledCartModifier>{' '}
          <List disablePadding dense sx={{ display: 'inline' }}>
            {field.selected.map((option, idx, arr) => {
              const price = getVariantPrice(option.price_variance);
              return (
                <ListItem
                  disableGutters
                  dense
                  key={option.option_slug}
                  sx={{ display: 'inline', padding: 0 }}
                >
                  {option.quantity && (
                    <StyledCartModifier
                      data-testid="cart-item-variant-quantity"
                      component="span"
                      {...styles}
                    >
                      {`${option.quantity}x `}
                    </StyledCartModifier>
                  )}
                  <StyledCartModifier
                    data-testid="cart-item-variant-name"
                    component="span"
                    {...styles}
                  >
                    {option.option_name || option.option_text}
                  </StyledCartModifier>
                  {price && (
                    <StyledCartModifier
                      data-testid="cart-item-variant-price"
                      component="span"
                      {...styles}
                    >
                      {` ($${option.price_variance})`}
                    </StyledCartModifier>
                  )}
                  {idx !== arr.length - 1 ? (
                    <StyledCartModifier component="span" {...styles}>
                      ,{' '}
                    </StyledCartModifier>
                  ) : (
                    ''
                  )}
                </ListItem>
              );
            })}
          </List>
        </ListItem>
      ))}
    </List>
  );
}

RequiredFields.propTypes = {
  requiredFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string
    })
  )
};

function CartItem({
  item,
  updateQuantity = () => {},
  itemActions = null,
  showCartQuantity = false,
  hasInvestorDayFeature = false
}) {
  const {
    name,
    order_made_for: orderMadeFor,
    quantity,
    required_fields: fields,
    special_instructions: specialInstructions,
    variant
  } = item;
  const styles = { variant: ITEM_DESCRIPTION_AND_PRICES };

  return (
    <Box data-testid="cart-item" width="100%">
      <Box display="flex" justify="space-between" pt={2} pb={2}>
        <Box display="flex" alignContent="flex-start" flexBasis="30px">
          {showCartQuantity ? (
            <CartItemQuantity
              data-testid="cart-item-quantity"
              item={item}
              updateQuantity={updateQuantity}
            />
          ) : (
            <StyledTypographyColorSectionNavLinkMainMedium
              data-testid="cart-item-quantity"
              {...styles}
            >
              {quantity}x
            </StyledTypographyColorSectionNavLinkMainMedium>
          )}
        </Box>
        <Box flex="2">
          <StyledTypographyColorSectionNavLinkMain
            data-testid="cart-item-name"
            {...styles}
          >
            {name}
          </StyledTypographyColorSectionNavLinkMain>
          {!!variant.name && (
            <StyledCartVariantName {...styles}>
              {`Size: ${variant.name}`}
            </StyledCartVariantName>
          )}
          {fields && !!fields.length && (
            <RequiredFields requiredFields={fields} />
          )}
          {orderMadeFor && (
            <StyledTypographyColorSectionNavLinkMainMedium
              data-testid="cart-item-made-for"
              {...styles}
              sx={{ display: 'block' }}
            >
              Made For: {orderMadeFor}
            </StyledTypographyColorSectionNavLinkMainMedium>
          )}
          {specialInstructions && (
            <StyledTypographyColorSectionNavLinkMainMedium
              data-testid="cart-item-special-intructions"
              {...styles}
              sx={{ display: 'block' }}
            >
              {specialInstructions}
            </StyledTypographyColorSectionNavLinkMainMedium>
          )}
          {itemActions && (
            <Box
              data-testid="cart-item-actions"
              position="relative"
              left="-0.85rem"
            >
              {itemActions}
            </Box>
          )}
        </Box>
        {variant && !hasInvestorDayFeature && (
          <Box component={Grid} item display="flex" alignContent="flex-start">
            <StyledTypographyColorSectionNavLinkMainMedium
              data-testid="cart-item-price"
              {...styles}
            >
              {showCartQuantity
                ? `${quantity} x $${variant.price}`
                : `$${variant.price}`}
            </StyledTypographyColorSectionNavLinkMainMedium>
          </Box>
        )}
      </Box>
    </Box>
  );
}

CartItem.propTypes = {
  item: PropTypes.shape({
    quantity: PropTypes.number,
    name: PropTypes.string,
    variant: PropTypes.shape({
      price: PropTypes.string,
      name: PropTypes.string
    }),
    order_made_for: PropTypes.string,
    special_instructions: PropTypes.string,
    required_fields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string
      })
    )
  }),
  itemActions: PropTypes.element,
  showCartQuantity: PropTypes.bool,
  updateQuantity: PropTypes.func,

  hasInvestorDayFeature: PropTypes.bool
};

export default CartItem;
